import type {
  PlanningRange,
  PlanningRangeDto,
  StoreStudentMeetingDto,
  StudentMeetingFormData,
} from "meetings/student-meeting/types";
import { formatDateInput, fromDutchToIntlDate } from "shared/utils/dateHelpers";

/** DD-MM-YYYY (picker) → Y-m-d (API), null-safe. */
const toApiDate = (dutchDate: string | null): string | null => {
  if (!dutchDate) {
    return null;
  }
  const parsed = fromDutchToIntlDate(dutchDate, "00:00");
  return parsed ? formatDateInput(parsed) : null;
};

const rangeToDto = (range: PlanningRange): PlanningRangeDto => ({
  start_date: toApiDate(range.start_date) ?? "",
  end_date: toApiDate(range.end_date),
  start_time: range.start_time || null,
  end_time: range.end_time || null,
});

// DateFromTo seeds an empty row on mount; untouched rows must not reach the
// API (start_date is required per submitted row).
const filledRangesToDto = (ranges: PlanningRange[]): PlanningRangeDto[] =>
  ranges.filter((range) => Boolean(range.start_date)).map(rangeToDto);

/**
 * Maps form state to the POST api/meetings contract. The removed always-on
 * options (grades, reflection, invite, sync, reminders) are deliberately not
 * part of the payload — the server forces them (MEETING-CREATE-DEFAULTS-001).
 */
export const studentMeetingFormDataToDto = (
  form: StudentMeetingFormData,
): StoreStudentMeetingDto => {
  const specific = form.opensAtMode === "specific";

  return {
    name: form.name,
    message: form.message,
    mail_student: form.mailStudent === 1,
    // Empty stays "" — the column is NOT NULL (live stored "" as well).
    mail_student_text: form.mailStudentText ?? "",
    // Hidden (and off) when sharing is off; the server enforces the same.
    allow_student_ratings:
      form.mailStudent === 1 && form.allowStudentRatings === 1,
    mentor_classes: [...form.mentorClasses],
    schedules: [...form.schedules],
    timeframe_settings: {
      opens_at_mode: form.opensAtMode,
      opens_at_date: specific ? toApiDate(form.opensAtDate) : null,
      opens_at_time: specific ? form.opensAtTime : null,
      closed_modify_at_date: toApiDate(form.closedModifyDate),
      closed_modify_at_time: form.closedModifyDate
        ? form.closedModifyTime
        : null,
      closed_no_modify_at_date: toApiDate(form.closedNoModifyDate),
      closed_no_modify_at_time: form.closedNoModifyTime,
      share_feedback_at_date:
        form.mailStudent === 1 ? toApiDate(form.shareFeedbackDate) : null,
      share_feedback_at_time:
        form.mailStudent === 1 ? form.shareFeedbackTime : null,
      auto_share: form.autoShare === 1,
      archived_at_date: toApiDate(form.archivedDate),
      archived_at_time: form.archivedTime,
      triangle_conversation_from: toApiDate(form.triangleFrom),
      triangle_conversation_till: toApiDate(form.triangleTill),
      feedback_reservations: filledRangesToDto(form.feedbackReservations),
      speed_dates: filledRangesToDto(form.speedDates),
      speed_date_preparations: filledRangesToDto(form.speedDatePreparations),
    },
  };
};
